import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  useTheme,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Help as HelpIcon
} from '@mui/icons-material';

const ChartContainer = ({
  title,
  subtitle,
  children,
  actions,
  loading = false,
  error,
  height,
  elevation = 2,
  description
}) => {
  const theme = useTheme();

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: height ? height - 120 : 200, // Account for header height or default loading height
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading chart data...
          </Typography>
        </Box>
      );
    }

    if (error) {
      const errorMessage =
        error?.message || error?.toString?.() || "Failed to load chart data";
      
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          <Typography variant="body2" color="text.secondary" align="center">
            Unable to load chart data. Please try refreshing.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: height ? height - 120 : 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {children}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        height: height || 'auto',
        boxShadow: theme.shadows[elevation],
        '&:hover': {
          boxShadow: theme.shadows[elevation + 1],
          transition: 'box-shadow 0.3s ease'
        }
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {description && (
              <Tooltip 
                title={description} 
                arrow 
                placement="top"
                enterDelay={300}
                leaveDelay={200}
                sx={{
                  '& .MuiTooltip-tooltip': {
                    maxWidth: 400,
                    fontSize: '0.875rem',
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    padding: theme.spacing(1.5),
                    borderRadius: theme.spacing(1)
                  }
                }}
              >
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.action.hover
                    },
                    p: 0.5
                  }}
                >
                  <HelpIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        subheader={
          subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )
        }
        action={actions}
        sx={{
          pb: 1,
          '& .MuiCardHeader-content': {
            overflow: 'hidden'
          },
          '& .MuiCardHeader-title': {
            fontSize: '1.1rem'
          }
        }}
      />
      <CardContent sx={{ pt: 0, pb: 2, height: height ? 'calc(100% - 80px)' : 'auto' }}>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default ChartContainer;
